import React, { useEffect } from "react";
import { EmptyList, Loading } from "../../../components";
import { ProductsFiltersInput } from "../../../__generated__/globalTypes";
import { useLazyProducts, useProducts } from "../hooks";
import { dateToText } from "../../../helpers/date";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  Calendar,
  Euro,
  Image,
  Layers3,
  Package,
} from "lucide-react";
import { useLazyProduct } from "../hooks/useProduct";

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor("productImgUrl", {
    // cell: (info) => info.getValue(),
    cell: (info) => (
      <span className="flex items-center justify-center">
        <img src={info.getValue()} alt="" width={35} height={35} className="" />
      </span>
    ),
    header: () => (
      <span className="flex items-center">
        <Image className="mr-2" size={16} />
      </span>
    ),
  }),
  columnHelper.accessor("product", {
    cell: (info) => {
      // @ts-ignore
      const value = info.getValue().name || "";
      // @ts-ignore
      const displayValue = value
        ? // @ts-ignore
          value?.substring(0, 35).toLocaleLowerCase()
        : "";
      const tooltipValue = value ? value : "";
      // @ts-ignore
      const showEllipsis = value.length > 35;
      return (
        <a
          href={`/product/${
            //@ts-ignore
            info.getValue().id
          }`}
          className="tooltip text-xs text-center cursor-pointer"
        >
          {displayValue} {showEllipsis ? "..." : ""}
          <span className="tooltiptext">{tooltipValue}</span>
        </a>
      );
    },
    header: () => (
      <span className="flex items-center justify-center">
        <Package className="mr-2" size={16} /> Produit
      </span>
    ),
  }),
  columnHelper.accessor("lastPrice", {
    id: "lastPrice",
    cell: (info) => (
      <span className="italic text-center flex items-center  justify-center">
        {parseFloat(info.getValue()).toFixed(2) || info.getValue()} €
      </span>
    ),
    header: () => (
      <span className="flex items-center">
        <Euro className="mr-2" size={16} /> Prix
      </span>
    ),
  }),
  columnHelper.accessor("lastQuantity", {
    id: "lastQuantity",
    cell: (info) => (
      <span className="italic text-center flex items-center  justify-center">
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className="flex items-center">
        <Layers3 className="mr-2" size={16} /> Stock
      </span>
    ),
  }),
  columnHelper.accessor("createdAt", {
    id: "createdAt",
    cell: (info) => (
      <span className="italic text-center text-blue-600">
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className="flex items-center">
        <Calendar className="mr-2" size={16} /> Date Ajout
      </span>
    ),
  }),
];

export const ProductsPreview: React.FC<ProductsFiltersInput> = (where) => {
  // const [data] = React.useState(() => [...iData]);
  const limit = 25;
  const [sorting, setSorting] = React.useState([]);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const skipPageResetRef = React.useRef();
  const [orderByPrice, setOrderByPrice] = React.useState<any>(null);
  const [orderByQuantity, setOrderByQuantity] = React.useState<any>(null);
  const [orderByCreatedAt, setOrderByCreatedAt] = React.useState<any>(null);

  const [search, { data, loading, fetchMore, error }] = useLazyProducts();

  useEffect(() => {
    search({
      variables: {
        where: {
          ...where,
          orderByCreatedAt,
          orderByPrice,
          orderByQuantity,
        },
        limit,
        offset: 0,
      },
    });
  }, []);

  useEffect(() => {
    if (orderByCreatedAt === "-") {
      setOrderByCreatedAt(null);
    }
    if (orderByPrice === "-") {
      setOrderByPrice(null);
    }
    if (orderByQuantity === "-") {
      setOrderByQuantity(null);
    }

    search({
      variables: {
        where: {
          ...where,
          orderByCreatedAt,
          orderByPrice,
          orderByQuantity,
        },
        limit,
        offset: 0,
      },
    });
  }, [orderByCreatedAt, orderByPrice, orderByQuantity]);

  console.log("data", data);

  const table = useReactTable({
    data:
      data?.products?.results?.map((product) => ({
        productImgUrl: product?.imgUrl,
        product: {
          id: product?.id,
          name: product?.name,
        },
        lastPrice: product?.lastPrice,
        lastQuantity: product?.lastQuantity,
        createdAt: dateToText(product?.createdAt),
      })) || [],
    // @ts-ignore
    columns,
    state: {
      sorting,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 5,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    // @ts-ignore
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),

    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    // re render the component when the data changes
    // forceUpdate((prev) => !prev),
    // autoResetPageIndex: !skipPageResetRef.current,
    // autoResetExpanded: !skipPageResetRef.current,
  });

  if (loading) {
    return <Loading />;
  }

  if (data?.products?.results?.length === 0) {
    return <EmptyList text="Aucun Produit" />;
  }

  return (
    <div className="flex flex-col mx-auto py-2 sm:px-6 lg:px-2">
      <div className="flex flex-wrap mb-2">
        <div className="w-1/3 p-1">
          <span className="label">Trier par Prix</span>{" "}
          <select
            className="input w-full"
            value={orderByPrice}
            onChange={(e) => setOrderByPrice(e.target.value)}
          >
            <option value="-">-</option>
            <option value="ASC">ASC</option>
            <option value="DESC">DESC</option>
          </select>
        </div>
        <div className="w-1/3 p-1">
          <span className="label">Trier par Date</span>{" "}
          <select
            className="input w-full"
            value={orderByCreatedAt}
            onChange={(e) => setOrderByCreatedAt(e.target.value)}
          >
            <option value="-">-</option>
            <option value="ASC">ASC</option>
            <option value="DESC">DESC</option>
          </select>
        </div>
        <div className="w-1/3 p-1">
          <span className="label">Trier par Quantité</span>{" "}
          <select
            className="input w-full"
            value={orderByQuantity}
            onChange={(e) => setOrderByQuantity(e.target.value)}
          >
            <option value="-">-</option>
            <option value="ASC">ASC</option>
            <option value="DESC">DESC</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 tracking-wider"
                  >
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none flex items-center justify-center"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      <ArrowUpDown className="ml-2" size={14} />
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-2 py-1 whitespace-nowrap text-xs text-gray-500 text-center"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="w-full flex justify-center items-center">
        {data?.products?.hasMore ? (
          <button
            className="btn mt-2 px-4"
            onClick={() =>
              fetchMore({
                variables: {
                  limit,
                  offset: data?.products?.results?.length,
                  where: {
                    ...where,
                    orderByCreatedAt,
                    orderByPrice,
                    orderByQuantity,
                  },
                },
              })
            }
          >
            Plus
          </button>
        ) : null}
      </div>
    </div>
  );
};
